import { catalogApiClient } from "./catalogApiClient";
import {
  CatalogReviewControllerApi,
  WhoDrugRecodeControllerApi,
  CatalogReviewRoutingControllerApi,
} from "@/lib/api/generated/casedb";
import type {
  CatalogCapabilities,
  CatalogCaseHeader,
  CatalogFieldState,
  CatalogOption,
  CatalogFinding,
  CatalogReviewPatchRequest,
  CatalogReviewResponse,
} from "@/lib/api/generated/casedb";
import { getOrCreateSessionId } from "@/features/catalog_case_review/shared/services/caseLockService";
import { toCatalogApiError } from "../utils/catalogErrors";
import type { CatalogReviewDocument, CatalogSection } from "../types/catalogReviewTypes";
import { CASE_REVIEW_SECTION_IDS } from "../constants/caseReviewSections";

/**
 * Every catalog-review call, through the generated client.
 *
 * There is no URL string in this file, or anywhere else in the feature tree. The contract is
 * generated from the server's OpenAPI export, so a route that changes shape breaks the build here
 * rather than at runtime in front of a reviewer.
 */

/**
 * The generated {@code Configuration} adds a {@code User-Agent} header, which no browser lets a
 * page set — every call logs "Refused to set unsafe header" and the header never goes anyway.
 * Passing our own empty header set replaces theirs, because the constructor spreads
 * {@code baseOptions} after its own defaults.
 */

async function reviewApi(): Promise<CatalogReviewControllerApi> {
  return catalogApiClient(CatalogReviewControllerApi);
}

async function routeApi(): Promise<CatalogReviewRoutingControllerApi> {
  return catalogApiClient(CatalogReviewRoutingControllerApi);
}



/**
 * Unwraps the standard envelope once, so nothing downstream knows it exists.
 *
 * Typed on the generated envelope's own members rather than `unknown`: casting six fields into
 * place would make a server-side rename compile cleanly and fail at runtime, which is the drift
 * the generated client exists to prevent. One function for every section, because a second copy
 * is a second place for "the server always sends this" to stop being true.
 */
function unwrapSection<TValues, TContext>(
  body: CatalogSectionBody<TValues, TContext> | undefined,
  section: string,
): CatalogSection<TValues, TContext> {
  if (!body) {
    throw new Error(`The ${section} response carried no section.`);
  }
  return {
    catalogVersion: required(body.catalog_version, "catalog_version", section),
    revision: required(body.revision, "revision", section),
    values: required(body.values, "values", section),
    fieldStates: required(body.field_states, "field_states", section),
    fieldOptions: required(body.field_options, "field_options", section),
    capabilities: required(body.capabilities, "capabilities", section),
    context: required(body.context, "context", section),
    caseHeader: required(body.case_header, "case_header", section),
  };
}

/**
 * The envelope body every section shares, spelled once.
 *
 * The generated types name one class per section — the response is generic in Java and the
 * OpenAPI export flattens that — so this describes the members they have in common and stays
 * assignable from each of them.
 */
interface CatalogSectionBody<TValues, TContext> {
  catalog_version?: string;
  revision?: number;
  values?: TValues;
  field_states?: Record<string, CatalogFieldState>;
  field_options?: Record<string, CatalogOption[]>;
  capabilities?: CatalogCapabilities;
  context?: TContext;
  case_header?: CatalogCaseHeader;
}

/**
 * The server always sends these; the generated types mark them optional because the OpenAPI
 * schema does. Failing here names what is missing, which beats defaulting to an empty object and
 * rendering a section that looks merely empty.
 */
function required<T>(value: T | undefined, field: string, section: string): T {
  if (value === undefined || value === null) {
    throw new Error(`The ${section} response is missing ${field}.`);
  }
  return value;
}

/**
 * The review document, one entry per registered section.
 *
 * Driven by the section registry rather than a list of literals: a section that is registered but
 * not unwrapped here would reach the page as `undefined` and show as an empty tab, which is
 * indistinguishable from a case with nothing in it. Missing is therefore an error that names the
 * section.
 */
function unwrapReview(payload: CatalogReviewResponse): CatalogReviewDocument {
  const sections = Object.fromEntries(CASE_REVIEW_SECTION_IDS.map((sectionId) => {
    const raw = payload.sections?.[sectionId];
    if (!raw) throw new Error(`The review response carried no ${sectionId} section.`);
    return [sectionId, unwrapSection(raw as CatalogSectionBody<unknown, unknown>, sectionId)];
  })) as unknown as CatalogReviewDocument["sections"];
  return {
    catalogVersion: required(payload.catalog_version, "catalog_version", "review"),
    revision: required(payload.revision, "revision", "review"),
    caseHeader: required(payload.case_header, "case_header", "review"),
    followups: required(payload.followups, "followups", "review"),
    sections,
  };
}

export async function getReview(caseId: number, signal?: AbortSignal): Promise<CatalogReviewDocument> {
  try {
    const response = await (await reviewApi()).get(caseId, getOrCreateSessionId(), { signal });
    return unwrapReview(response.data.data as CatalogReviewResponse);
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

export async function patchReview(caseId: number, request: CatalogReviewPatchRequest): Promise<CatalogReviewDocument> {
  try {
    const response = await (await reviewApi()).patch(caseId, request, getOrCreateSessionId());
    return unwrapReview(response.data.data as CatalogReviewResponse);
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

export async function routeCaseForward(caseId: number, revision: number, reason: string): Promise<void> {
  try {
    await (await routeApi()).route(caseId, { revision, reason }, getOrCreateSessionId());
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

export interface RouteReadinessResult {
  ready: boolean;
  findings: CatalogFinding[];
}

/** Runs the routing gate against the persisted revision without changing the case stage. */
export async function checkRouteReadiness(caseId: number, revision: number): Promise<RouteReadinessResult> {
  try {
    const response = await (await routeApi()).readiness(caseId, { revision });
    const body = required(response.data.data, "data", "route readiness");
    return {
      ready: required(body.ready, "ready", "route readiness"),
      findings: required(body.findings, "findings", "route readiness"),
    };
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

export async function applyWhoDrugRecode(caseId: number, productId: number,
  catalogVersion: string, revision: number, releaseId: number, rid: string, fingerprint: string, deadlineChangeToken?: string) {
  try {
    const api = await catalogApiClient(WhoDrugRecodeControllerApi);
    const response = await api.applyWhoDrugRecode(caseId, productId,
      { catalog_version: catalogVersion, revision, release_id: releaseId, rid, fingerprint, deadline_change_token: deadlineChangeToken }, getOrCreateSessionId());
    return unwrapReview(required(response.data.data, "data", "WHODrug recoding"));
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

export async function previewWhoDrugRecode(caseId: number, productId: number, releaseId: number, rid: string) {
  try {
    const api = await catalogApiClient(WhoDrugRecodeControllerApi);
    const response = await api.previewWhoDrugRecode(caseId, productId, releaseId, rid, getOrCreateSessionId());
    return required(response.data.data, "data", "WHODrug preview");
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}
